import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { responseHero } from "../../utils/axiosInterceptor";
import { getHoldingDepositRegister } from "../../api/getHoldingDepositRegister";
import { HoldingDepositRegister } from "../../models/holdingdepositregister.model";
import { AddHoldingDepositModal } from "../../components/humandc/modals/humanthirdpage/AddInvestmentModal";
import { useNavigate } from "react-router-dom";
const formatCurrency = (value: number | null | undefined, currency: "EUR" | "USD") =>
    value == null
        ? "—"
        : new Intl.NumberFormat("hu-HU", { style: "currency", currency }).format(value);

const formatDate = (value: Date | string) => new Date(value).toLocaleDateString("hu-HU");

export function HumanThirdPage() {

    const navigate = useNavigate();

    const [data, setData] = useState<HoldingDepositRegister[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getHoldingDepositRegister();
            setData(response);
            setError(null);
        } catch (error) {
            setError(error?.toString() ?? 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleStateChange = () => fetchData();

    const makeRecord = async (recordData: any) => {
        setIsSubmitting(true);
        await responseHero.post(`${process.env.REACT_APP_API_URL}/humandc/holdingdepositregister`, { data: recordData })
            .then(() => {
                setIsModalOpen(false);
                handleStateChange();
                toast.success(`Sikeresen hozzáadtál egy befizetést!`);
            })
            .catch(error => {
                console.error(JSON.stringify(error.response?.data?.message, null, 2));
                toast.error(`Sikertelen adatrögzítés!`);
            })
            .finally(() => setIsSubmitting(false));
    };

    const removeRecord = async (id: number) => {
        if (!window.confirm("Biztosan törlöd ezt a befizetést?")) return;

        setDeletingId(id);
        await responseHero.delete(`${process.env.REACT_APP_API_URL}/humandc/holdingdepositregister/${id}`)
            .then(() => {
                handleStateChange();
                toast.success(`Sikeresen törölted a befizetést!`);
            })
            .catch(error => {
                console.error(JSON.stringify(error.response?.data?.message, null, 2));
                toast.error(`Sikertelen törlés!`);
            })
            .finally(() => setDeletingId(null));
    };

    return (
        <div className="p-6">
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">További Human D.C. befizetések megadása már meglévő szerződésekhez. </h1>
                <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="rounded-lg bg-brown px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-dark-brown focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    Új befizetés hozzáadása
                </button>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">ID</th>

                            <th className="px-4 py-3 text-left font-medium text-gray-600">Dátum</th>

                            <th className="px-4 py-3 text-left font-medium text-gray-600">Kapcsolódó befektetés</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Név</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-600">Befizetett EUR</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-600">Befizetett EUR a levonásokkal</th>
                            
                            <th className="px-4 py-3 text-right font-medium text-gray-600">Befizetett összeg USD-ben</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-600">Műveletek</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {loading && (
                            <tr>
                                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">Betöltés...</td>
                            </tr>
                        )}

                        {!loading && data.length === 0 && (
                            <tr>
                                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">Nincs megjeleníthető befizetés.</td>
                            </tr>
                        )}

                        {!loading && data.map(item => (
                            <tr key={item.id} className="transition hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-900">{item.id}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-gray-700">{formatDate(item.date)}</td>
                                <td className="px-4 py-3 text-gray-700">{item.depositRegisterId}</td>
                                <td className="px-4 py-3 text-gray-700">{item.user?.name ?? "—"}</td>
                                <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                                    {formatCurrency(item.invested_amount_eur, "EUR")}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                                    {formatCurrency(item.net_invested_amount_eur, "EUR")}
                                </td>
                                
                                <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                                    {formatCurrency(item.invested_amount_usdt, "USD")}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex justify-end gap-2">
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/hcp/${item.id}`)}
                                            className="rounded-md border border-indigo-200 px-3 py-1 text-xs font-medium text-indigo-600 transition hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                                        >
                                            Coinkezelő
                                        </button>
                                        <button
                                            type="button"
                                            disabled={deletingId === item.id}
                                            onClick={() => removeRecord(item.id)}
                                            className="rounded-md border border-red-200 px-3 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                        >
                                            {deletingId === item.id ? "Törlés..." : "Törlés"}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <AddHoldingDepositModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={makeRecord}
                isSubmitting={isSubmitting}
            />
        </div>
    )
}